import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useProfile } from '../../profile/components/ProfileContext';
import { Category } from '../models/category.model';
import { UtilService } from '../../../../../core/services/util.service';
import { CategoryService } from '../services/category.service';
import { JobClientsService } from '../services/job-clients.service';
import { ClientId } from '../../company/models/clients.model';
import { JobService } from '../services/job.service';
import { Job } from '../models/job.model';
import { useJobStore } from '../../../../../core/store/job.store';
import JobSkills from './JobSkills';
import JobFlyerPanel from './JobFlyerPanel';
import { SelectValue } from '../../../../../core/models/components/select-value.model';
import RichTextEditor from '../../../../../core/components/Quill/RichTextEditor';
import { isProgrammaticRichTextFocus } from '../../../../../core/components/Quill/rich-text-focus';
import { Cascader, Dropdown, Input, Select } from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileImageOutlined,
  LinkOutlined,
  MoreOutlined,
  RollbackOutlined,
  SaveOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { route } from '../../../../../constants/routes';
import {
  formatJobCompensation,
  formatJobRegion,
  getJobReadiness,
  validateJob,
} from './job-form.utils';
import './JobForm.css';

interface JobFormProps {
  onJobUpdate?: (job: Job) => void;
}

type JobFormSection = 'basics' | 'description' | 'skills' | 'review';

const LATAM_COUNTRIES = [
  'Argentina',
  'Brazil',
  'Chile',
  'Colombia',
  'Costa Rica',
  'Ecuador',
  'El Salvador',
  'Guatemala',
  'Honduras',
  'Mexico',
  'Nicaragua',
  'Panama',
  'Paraguay',
  'Peru',
  'Uruguay',
  'Venezuela',
];

const COUNTRY_OPTIONS = [
  {
    label: 'LATAM',
    value: 'LATAM',
    children: LATAM_COUNTRIES.map((country) => ({
      label: country,
      value: country,
    })),
  },
  {
    label: 'United States',
    value: 'United States',
  },
];

const LOCATION_OPTIONS: SelectValue[] = [
  { label: 'Remote', value: 'Remote' },
  { label: 'On-Site', value: 'On-Site' },
  { label: 'Hybrid', value: 'Hybrid' },
];

const CURRENCY_OPTIONS: SelectValue[] = [
  { label: '₡', value: 'CRC_COLON' },
  { label: '$', value: 'US_DOLLAR' },
];

const SECTION_STEPS: { key: JobFormSection; label: string }[] = [
  { key: 'basics', label: 'Basics' },
  { key: 'description', label: 'Description' },
  { key: 'skills', label: 'Skills' },
  { key: 'review', label: 'Review' },
];

const formatMoney = (
  value: string | number | undefined | null,
  currency: string
): string => {
  if (!value) return '';

  const numericValue = String(value).replace(/[^\d]/g, '');
  if (!numericValue) return '';

  const amount = parseInt(numericValue, 10);
  return amount.toLocaleString(currency === 'CRC_COLON' ? 'es-CR' : 'en-US');
};

const parseMoney = (value: string): string => value.replace(/[^\d]/g, '');

const JobForm: React.FC<JobFormProps> = ({ onJobUpdate }) => {
  const { companyId, companyName } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const fromDashboard = location.state?.fromDashboard;
  const isDeleted = location.state?.deleted || false;
  const [item, setItem] = useState<Job>(() => new Job());
  const [categories, setCategories] = useState<Category[]>([]);
  const [clients, setClients] = useState<ClientId[]>([]);
  const [skills, setSkills] = useState<
    { id: string; skillName: string; skillLevel: string }[]
  >([]);
  const [initialFormState, setInitialFormState] = useState<Job>(
    () => new Job()
  );
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [errorMessages, setErrorMessages] = useState<{ [key: string]: string }>(
    {}
  );
  const [activeSection, setActiveSection] = useState<JobFormSection>('basics');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const basicsSectionRef = useRef<HTMLElement>(null);
  const descriptionSectionRef = useRef<HTMLElement>(null);
  const skillsSectionRef = useRef<HTMLElement>(null);
  const reviewSectionRef = useRef<HTMLElement>(null);
  const flyerPanelRef = useRef<HTMLDivElement>(null);

  const scrollToSection = (section: JobFormSection) => {
    const sectionRefs: Record<JobFormSection, React.RefObject<HTMLElement>> = {
      basics: basicsSectionRef,
      description: descriptionSectionRef,
      skills: skillsSectionRef,
      review: reviewSectionRef,
    };

    setActiveSection(section);
    sectionRefs[section].current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const scrollToFlyer = () => {
    setActiveSection('review');
    flyerPanelRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const activateSectionOnUserFocus =
    (section: JobFormSection) => (event: React.FocusEvent<HTMLElement>) => {
      if (isProgrammaticRichTextFocus(event.target)) {
        return;
      }
      setActiveSection(section);
    };

  useEffect(() => {
    let ignore = false;

    const getJob = async () => {
      if (!id) return;
      try {
        const job: Job = await JobService.GetJob(id);
        if (ignore) return;

        const jobWithClientId = {
          ...job,
          clientId: job.client.id,
        };
        setItem(jobWithClientId);
        setInitialFormState(jobWithClientId);
        setSkills(job.skills);
        // Notify parent component about job update
        onJobUpdate?.(jobWithClientId);
      } catch (error: unknown) {
        if (ignore) return;
        console.error('Error fetching job data:', error);
        UtilService.Alert('Error', 'error', 'Failed to load job data.');
      }
    };

    void getJob();

    return () => {
      ignore = true;
    };
  }, [id, onJobUpdate]);

  useEffect(() => {
    if (skills.length > 0 && validationErrors.includes('skills')) {
      setValidationErrors((prev) => prev.filter((err) => err !== 'skills'));
      setErrorMessages((prev) => {
        const { skills, ...rest } = prev;
        return rest;
      });
    }
  }, [skills, validationErrors]);

  useEffect(() => {
    let ignore = false;

    if (companyId) {
      const getClientsData = async () => {
        try {
          const response = await JobClientsService.Get(companyId);
          if (ignore) return;
          setClients(response);
        } catch (error: unknown) {
          if (ignore) return;
          console.error('Error fetching clients data:', error);
          UtilService.Alert('Error', 'error', 'Failed to load clients data.');
        }
      };
      void getClientsData();
    }

    return () => {
      ignore = true;
    };
  }, [companyId]);

  useEffect(() => {
    let ignore = false;

    const getCategories = async () => {
      const response = await CategoryService.Get();
      if (!ignore) {
        setCategories(response);
      }
    };
    void getCategories();

    return () => {
      ignore = true;
    };
  }, []);

  const categoryOptions = useMemo(
    () =>
      categories.map((category) => ({
        label: category.name,
        value: category.id,
      })),
    [categories]
  );
  const subCategories = useMemo(() => {
    const selectedCategory = categories.find(
      (category) => category.name === item.category
    );
    if (!selectedCategory) return [];

    return selectedCategory.subCategories
      .map((subcategory) => ({
        label: subcategory,
        value: subcategory,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [categories, item.category]);

  const onChangeInput = (name: string, value: string) => {
    setItem((previousItem) => ({ ...previousItem, [name]: value }));
  };

  const onChangeSalary = (name: string, value: string) => {
    const numericValue = parseMoney(value);
    setItem((previousItem) => ({ ...previousItem, [name]: numericValue }));
  };

  const onChangeSelect = (name: string, value: string) => {
    setItem((previousItem) => ({ ...previousItem, [name]: value }));
  };

  const onChangeClient = (value: string) => {
    const selectedClient = clients.find((client) => client.id === value);
    const clientCurrency = selectedClient?.currency || '';

    setItem((previousItem) => ({
      ...previousItem,
      clientId: value,
      currency: clientCurrency,
    }));
  };

  const onChangeQuill = (name: string, value: string) => {
    setItem((previousItem) => ({ ...previousItem, [name]: value }));
  };

  const cascaderValue = useMemo(() => {
    const values: string[][] = [];

    if (item.countries && item.countries.length > 0) {
      item.countries.forEach((country) => {
        if (LATAM_COUNTRIES.includes(country)) {
          values.push(['LATAM', country]);
        } else {
          values.push([country]);
        }
      });

      if (item.region === 'LATAM') {
        values.push(['LATAM']);
      }

      return values;
    }

    return [];
  }, [item.countries, item.region]);

  const onChangeCountries = (value: string[][]) => {
    const selectedValues = value.flatMap((entry) =>
      entry.length > 0 ? [entry[entry.length - 1]] : []
    );
    const hasLATAM = selectedValues.includes('LATAM');
    const countries = selectedValues.filter((country) => country !== 'LATAM');

    setItem((previousItem) => ({
      ...previousItem,
      countries,
      region: hasLATAM ? 'LATAM' : null,
    }));
  };

  const save = async (
    item: Job,
    draft: boolean = false,
    published: boolean = false
  ) => {
    const job: Job = {
      ...item,
      skills: skills,
      draft,
      published,
    };

    const { errors, messages } = validateJob(job);
    setValidationErrors(errors);
    setErrorMessages(messages);
    if (errors.length > 0) {
      return;
    }

    setIsSubmitting(true);

    if (id) {
      try {
        await JobService.Edit(job, id);
        const updatedJob = await JobService.GetJob(id);
        const jobWithClientId = {
          ...updatedJob,
          clientId: updatedJob.client.id,
        };
        setItem(jobWithClientId);
        setInitialFormState(jobWithClientId);
        setSkills(updatedJob.skills);
        setValidationErrors([]);
        setErrorMessages({});
        // Notify parent component about job update
        onJobUpdate?.(jobWithClientId);

        if (draft) {
          UtilService.Alert(
            'Draft Saved!',
            'success',
            'The job has been saved as a draft.',
            undefined,
            () => {}
          );
        } else if (published) {
          UtilService.Alert(
            'Job Published!',
            'success',
            'The job has been published.',
            undefined,
            () => {}
          );
        } else {
          UtilService.Alert(
            'Success!',
            'success',
            'The job has been updated.',
            undefined,
            () => {}
          );
        }
      } catch (error: any) {
        console.error('Error updating job:', error);
        UtilService.Alert(
          'Error',
          'error',
          error.message || 'Failed to update the job.'
        );
      } finally {
        setIsSubmitting(false);
      }
    } else {
      try {
        const newJob = await JobService.Add(job);
        if (!newJob) {
          throw new Error('Job creation failed - no response from server');
        }

        if (draft) {
          UtilService.Alert(
            'Draft Created!',
            'success',
            'The job has been saved as a draft.',
            undefined,
            () => {
              navigate(route(`/job/${newJob.id}`), {
                state: {
                  jobName: newJob.jobName,
                  ...(fromDashboard ? { fromDashboard: true } : {}),
                },
              });
            }
          );
        } else if (published) {
          UtilService.Alert(
            'Job Published!',
            'success',
            'The job has been created and published successfully.',
            undefined,
            () => {
              navigate(route(`/job/${newJob.id}`), {
                state: {
                  jobName: newJob.jobName,
                  ...(fromDashboard ? { fromDashboard: true } : {}),
                },
              });
            }
          );
        } else {
          UtilService.Alert(
            'Success!',
            'success',
            'The job has been created.',
            undefined,
            () => {
              navigate(route(`/job/${newJob.id}`), {
                state: {
                  jobName: newJob.jobName,
                  ...(fromDashboard ? { fromDashboard: true } : {}),
                },
              });
            }
          );
        }

        setValidationErrors([]);
        setErrorMessages({});
      } catch (error: any) {
        console.error('Job creation error:', error);
        UtilService.Alert(
          'Error',
          'error',
          error.message ||
            'Failed to create the job. Please check your credentials or try again later.'
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handlePreview = () => {
    if (id) {
      window.open(
        `/#${route(`/job/${item.id}/preview`)}`,
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }
    const previewJob = { ...item, skills };
    const { errors, messages } = validateJob(previewJob);
    setValidationErrors(errors);
    setErrorMessages(messages);
    if (errors.length > 0) {
      return;
    }

    useJobStore.getState().setJob(previewJob);

    window.open(`/#${route(`/job/preview`)}`, '_blank', 'noopener,noreferrer');
  };

  const archiveJob = async () => {
    await JobService.Delete(item.id);

    UtilService.Alert(
      'Success!',
      'success',
      'The Job has been deleted.',
      undefined,
      () => {
        goBack();
      }
    );
  };

  const restoreJob = async () => {
    try {
      await JobService.Restore(item.id);
      UtilService.Alert(
        'Job restored',
        'success',
        'The job is available in the active portfolio as an unpublished draft.'
      );
      goBack();
    } catch (error) {
      console.error('Error restoring job:', error);
      UtilService.Alert(
        'Restore unavailable',
        'error',
        'The job could not be moved back to Active.'
      );
    }
  };

  const goBack = () => {
    if (location.state?.fromDashboard) {
      navigate(route('/dashboard'));
    } else {
      navigate(route('/job'));
    }
  };

  const copyLink = async () => {
    const url = (item as any).url || item.url;

    if (url) {
      try {
        await navigator.clipboard.writeText(url);
        UtilService.Alert('Success!', 'success', 'Link copied to clipboard.');
      } catch (error) {
        console.error('Error copying link:', error);
        UtilService.Alert(
          'Error',
          'error',
          'Failed to copy link to clipboard.'
        );
      }
    } else {
      UtilService.Alert('Error', 'error', 'No link available to copy.');
    }
  };
  useEffect(() => {
    if (companyName) {
      setItem((prev) => ({ ...prev, company: companyName }));
    }
  }, [companyName]);

  const readiness = useMemo(
    () => getJobReadiness(item, skills),
    [item, skills]
  );
  const isFormModified = useMemo(
    () =>
      JSON.stringify({ ...item, skills }) !== JSON.stringify(initialFormState),
    [initialFormState, item, skills]
  );
  const selectedClientName =
    clients.find((client) => client.id === item.clientId)?.name ||
    item.client?.name ||
    'Not set';
  const selectedCategoryId = categories.find(
    (category) => category.name === item.category
  )?.id;
  const jobStatus = isDeleted
    ? 'Archived'
    : item.published
      ? 'Published'
      : item.draft
        ? 'Draft'
        : 'New job';
  const statusClass = isDeleted
    ? 'job-workspace__status--archived'
    : item.published
      ? 'job-workspace__status--published'
      : '';
  const pageTitle = isDeleted
    ? item.name || 'Archived job'
    : item.id
      ? item.name || 'Edit job'
      : 'Create a new job';
  const primaryActionLabel = item.published
    ? 'Update job'
    : item.id
      ? 'Publish job'
      : 'Create and publish';
  const primaryActionLoadingLabel = item.published
    ? 'Updating…'
    : item.id
      ? 'Publishing…'
      : 'Creating and publishing…';
  const shareMenuItems = [
    {
      key: 'copy-link',
      icon: <LinkOutlined />,
      label: 'Copy link',
      onClick: () => void copyLink(),
    },
    {
      key: 'open-job',
      icon: <EyeOutlined />,
      label: 'Open public job',
      onClick: () => {
        if (item.url) {
          window.open(item.url, '_blank', 'noopener,noreferrer');
          return;
        }
        UtilService.Alert('Error', 'error', 'No link available to open.');
      },
    },
    {
      key: 'whatsapp',
      icon: <WhatsAppOutlined />,
      label: 'Share on WhatsApp',
      onClick: () => {
        if (item.url) {
          const text = encodeURIComponent(`Check this job: ${item.url}`);
          window.open(
            `https://wa.me/?text=${text}`,
            '_blank',
            'noopener,noreferrer'
          );
          return;
        }
        UtilService.Alert('Error', 'error', 'No link available to share.');
      },
    },
  ];

  return (
    <main className="job-workspace">
      <header className="job-workspace__hero">
        <div className="job-workspace__hero-copy">
          <span className="job-workspace__kicker">Job workspace</span>
          <div className="job-workspace__title-row">
            <h1>{pageTitle}</h1>
            <span className={`job-workspace__status ${statusClass}`}>
              {jobStatus}
            </span>
          </div>
          <p>
            {isDeleted
              ? 'Review the archived job details and published assets.'
              : item.id
                ? 'Keep the role clear, current and ready for the right candidates.'
                : 'Build a clear job post that is ready to publish and match.'}
          </p>
        </div>

        <div className="job-workspace__actions" aria-label="Job actions">
          <button
            type="button"
            className="job-action job-action--quiet"
            onClick={goBack}
          >
            <ArrowLeftOutlined aria-hidden="true" />
            Back
          </button>
          {item.id ? (
            <Dropdown menu={{ items: shareMenuItems }} trigger={['click']}>
              <button
                type="button"
                className="job-action job-action--secondary"
                aria-label="Open job sharing options"
              >
                <LinkOutlined aria-hidden="true" />
                Job link
                <MoreOutlined aria-hidden="true" />
              </button>
            </Dropdown>
          ) : null}
          {isDeleted && item.id ? (
            <button
              type="button"
              className="job-action job-action--primary"
              onClick={() => void restoreJob()}
            >
              <RollbackOutlined aria-hidden="true" />
              Move to active
            </button>
          ) : null}
        </div>
      </header>

      <nav className="job-progress" aria-label="Job form sections">
        {SECTION_STEPS.map((step, index) => (
          <button
            key={step.key}
            type="button"
            className={`job-progress__button${
              activeSection === step.key ? ' job-progress__button--active' : ''
            }`}
            aria-current={activeSection === step.key ? 'step' : undefined}
            onClick={() => scrollToSection(step.key)}
          >
            <span className="job-progress__number">{index + 1}</span>
            <span className="job-progress__label">{step.label}</span>
          </button>
        ))}
      </nav>

      <div className="job-workspace__layout">
        <div className="job-workspace__main">
          <section
            ref={basicsSectionRef}
            className="job-section"
            onFocusCapture={activateSectionOnUserFocus('basics')}
          >
            <div className="job-section__header">
              <div>
                <h2>Basic information</h2>
                <p>Define the role, hiring client and working arrangement.</p>
              </div>
            </div>
            <div className="job-section__body">
              <div className="job-field-grid">
                <div className="job-field job-field--full">
                  <label htmlFor="job-name">Job title</label>
                  <Input
                    id="job-name"
                    placeholder="e.g. Principal Platform Engineer"
                    name="name"
                    value={item.name}
                    onChange={(event) =>
                      onChangeInput('name', event.target.value)
                    }
                    status={validationErrors.includes('name') ? 'error' : ''}
                    disabled={isDeleted}
                  />
                  <span className="job-field__hint">
                    Use a specific, searchable title candidates will recognize.
                  </span>
                  <span className="job-field__error">
                    {validationErrors.includes('name')
                      ? errorMessages.name
                      : ''}
                  </span>
                </div>

                <div className="job-field">
                  <label htmlFor="job-company">Company</label>
                  <Input
                    id="job-company"
                    name="company"
                    value={companyName || item.company || ''}
                    disabled
                  />
                </div>

                <div className="job-field">
                  <label htmlFor="job-client">Client</label>
                  <Select
                    id="job-client"
                    showSearch
                    allowClear
                    placeholder="Select a client"
                    style={{ width: '100%', textAlign: 'left' }}
                    value={item.clientId || item.client?.id || undefined}
                    options={clients.map((client) => ({
                      label: client.name,
                      value: client.id,
                    }))}
                    onChange={onChangeClient}
                    filterOption={(input, option) =>
                      (option?.label as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    status={
                      validationErrors.includes('clientId') ? 'error' : ''
                    }
                    disabled={isDeleted}
                  />
                  <span className="job-field__error">
                    {validationErrors.includes('clientId')
                      ? errorMessages.clientId
                      : ''}
                  </span>
                </div>

                <div className="job-field">
                  <label htmlFor="job-category">Category</label>
                  <Select
                    id="job-category"
                    showSearch
                    placeholder="Select a category"
                    style={{ width: '100%', textAlign: 'left' }}
                    options={categoryOptions}
                    value={selectedCategoryId}
                    onChange={(categoryId) => {
                      const selectedCategory = categories.find(
                        (category) => category.id === categoryId
                      );
                      if (!selectedCategory?.name) {
                        return;
                      }

                      setItem((previousItem) => ({
                        ...previousItem,
                        category: selectedCategory.name,
                        subCategory: '',
                      }));
                    }}
                    filterOption={(input, option) =>
                      (option?.label as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    status={
                      validationErrors.includes('category') ? 'error' : ''
                    }
                    disabled={isDeleted}
                  />
                  <span className="job-field__error">
                    {validationErrors.includes('category')
                      ? errorMessages.category
                      : ''}
                  </span>
                </div>

                <div className="job-field">
                  <label htmlFor="job-specialty">Specialty</label>
                  <Select
                    id="job-specialty"
                    showSearch
                    placeholder="Select a specialty"
                    style={{ width: '100%', textAlign: 'left' }}
                    options={subCategories}
                    value={item.subCategory || undefined}
                    onChange={(value) => onChangeSelect('subCategory', value)}
                    disabled={!item.category || isDeleted}
                    status={
                      validationErrors.includes('subCategory') ? 'error' : ''
                    }
                  />
                  <span className="job-field__error">
                    {validationErrors.includes('subCategory')
                      ? errorMessages.subCategory
                      : ''}
                  </span>
                </div>

                <div className="job-field job-field--full">
                  <span className="job-field__label">Workplace</span>
                  <div
                    className="job-workplace"
                    role="group"
                    aria-label="Workplace type"
                  >
                    {LOCATION_OPTIONS.map((workplace) => (
                      <button
                        key={workplace.value}
                        type="button"
                        className={`job-workplace__button${
                          item.location === workplace.value
                            ? ' job-workplace__button--active'
                            : ''
                        }`}
                        aria-pressed={item.location === workplace.value}
                        disabled={isDeleted}
                        onClick={() =>
                          onChangeSelect('location', workplace.value)
                        }
                      >
                        {workplace.label}
                      </button>
                    ))}
                  </div>
                  <span className="job-field__error">
                    {validationErrors.includes('location')
                      ? errorMessages.location
                      : ''}
                  </span>
                </div>

                <div className="job-field">
                  <label htmlFor="job-countries">Hiring region</label>
                  <Cascader
                    id="job-countries"
                    multiple
                    allowClear
                    showCheckedStrategy="SHOW_PARENT"
                    style={{ width: '100%', textAlign: 'left' }}
                    placeholder="Select countries or LATAM"
                    value={cascaderValue}
                    options={COUNTRY_OPTIONS}
                    onChange={onChangeCountries}
                    status={
                      validationErrors.includes('countries') ? 'error' : ''
                    }
                    disabled={isDeleted}
                  />
                  <span className="job-field__error">
                    {validationErrors.includes('countries')
                      ? errorMessages.countries
                      : ''}
                  </span>
                </div>

                <div className="job-field">
                  <span className="job-field__label">Salary range</span>
                  <div className="job-salary-grid">
                    <Select
                      aria-label="Salary currency"
                      placeholder="Currency"
                      value={item.currency || undefined}
                      options={CURRENCY_OPTIONS}
                      onChange={(value) => onChangeSelect('currency', value)}
                      disabled={isDeleted}
                    />
                    <Input
                      aria-label="Minimum salary"
                      placeholder="Minimum"
                      inputMode="numeric"
                      value={
                        item.minSalary
                          ? formatMoney(item.minSalary, item.currency || '')
                          : ''
                      }
                      onChange={(event) =>
                        onChangeSalary('minSalary', event.target.value)
                      }
                      disabled={isDeleted}
                    />
                    <Input
                      aria-label="Maximum salary"
                      placeholder="Maximum"
                      inputMode="numeric"
                      value={
                        item.maxSalary
                          ? formatMoney(item.maxSalary, item.currency || '')
                          : ''
                      }
                      onChange={(event) =>
                        onChangeSalary('maxSalary', event.target.value)
                      }
                      disabled={isDeleted}
                    />
                  </div>
                  <span className="job-field__hint">
                    Optional. Leave blank when compensation is discussed
                    privately.
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section
            ref={descriptionSectionRef}
            className="job-section"
            onFocusCapture={activateSectionOnUserFocus('description')}
          >
            <div className="job-section__header">
              <div>
                <h2>Job description</h2>
                <p>
                  Explain the impact, responsibilities and what success looks
                  like.
                </p>
              </div>
            </div>
            <div className="job-section__body">
              <div className="job-field">
                <label htmlFor="description">Description</label>
                <RichTextEditor
                  placeholder="Describe the role, responsibilities and requirements…"
                  id="description"
                  value={item.description}
                  onChange={(value) => onChangeQuill('description', value)}
                  className={`quill-custom${
                    validationErrors.includes('description')
                      ? ' border-red-500'
                      : ''
                  }`}
                  readOnly={isDeleted}
                />
                <span className="job-field__error">
                  {validationErrors.includes('description')
                    ? errorMessages.description
                    : ''}
                </span>
              </div>
            </div>
          </section>

          <section
            ref={skillsSectionRef}
            className="job-section job-section--skills"
            onFocusCapture={activateSectionOnUserFocus('skills')}
          >
            <div className="job-section__header">
              <div>
                <h2>Skills for matching</h2>
                <p>
                  Add the capabilities used to rank candidate recommendations.
                </p>
              </div>
            </div>
            <div className="job-section__body">
              <JobSkills
                skills={skills}
                setSkills={setSkills}
                isDeleted={isDeleted}
                description={item.description}
                jobTitle={item.name}
                category={item.category}
                jobId={id}
                heading={null}
              />
              <span className="job-field__error">
                {validationErrors.includes('skills')
                  ? errorMessages.skills
                  : ''}
              </span>
            </div>
          </section>

          <section
            ref={reviewSectionRef}
            className="job-section"
            onFocusCapture={activateSectionOnUserFocus('review')}
          >
            <div className="job-section__header">
              <div>
                <h2>Social flyer & review</h2>
                <p>
                  {item.id
                    ? 'Prepare the shareable creative after reviewing the publishing summary.'
                    : 'Save the job first to unlock its shareable social flyer.'}
                </p>
              </div>
            </div>
            <div className="job-section__body" ref={flyerPanelRef}>
              <JobFlyerPanel jobId={id || item.id} isDeleted={isDeleted} />
            </div>
          </section>
        </div>

        <aside className="job-workspace__aside" aria-label="Publishing summary">
          <section className="job-summary-card">
            <div className="job-summary-card__header">
              <h2>Job readiness</h2>
              <p>Complete the essentials before publishing.</p>
            </div>
            <div className="job-readiness">
              <div className="job-readiness__score">
                <div
                  className="job-readiness__ring"
                  role="progressbar"
                  aria-label="Job readiness"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={readiness.score}
                  style={
                    {
                      '--job-readiness': `${readiness.score}%`,
                    } as React.CSSProperties
                  }
                >
                  {readiness.score}%
                </div>
                <div>
                  <strong>
                    {readiness.isReady ? 'Ready to publish' : 'Keep building'}
                  </strong>
                  <span>
                    {readiness.completedRequirements} of{' '}
                    {readiness.totalRequirements} essentials complete
                  </span>
                </div>
              </div>
              <ul className="job-readiness__list">
                {readiness.items.map((readinessItem) => (
                  <li
                    key={readinessItem.key}
                    className={`job-readiness__item${
                      readinessItem.complete
                        ? ' job-readiness__item--complete'
                        : ''
                    }`}
                  >
                    <span className="job-readiness__check" aria-hidden="true">
                      {readinessItem.complete ? <CheckOutlined /> : '·'}
                    </span>
                    <div>
                      <strong>{readinessItem.label}</strong>
                      <span>{readinessItem.detail}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            {!isDeleted ? (
              <div className="job-readiness__actions">
                <button
                  type="button"
                  className="job-action job-action--primary"
                  disabled={!readiness.isReady || isSubmitting}
                  onClick={() => void save(item, false, true)}
                >
                  <CloudUploadOutlined aria-hidden="true" />
                  {isSubmitting
                    ? primaryActionLoadingLabel
                    : primaryActionLabel}
                </button>
                <div className="job-readiness__secondary-actions">
                  {!item.published ? (
                    <button
                      type="button"
                      className="job-action job-action--secondary"
                      disabled={
                        !isFormModified || !readiness.isReady || isSubmitting
                      }
                      onClick={() => void save(item, true, false)}
                    >
                      <SaveOutlined aria-hidden="true" />
                      Save draft
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="job-action job-action--secondary"
                    disabled={!readiness.isReady || isSubmitting}
                    onClick={handlePreview}
                  >
                    <EyeOutlined aria-hidden="true" />
                    Preview
                  </button>
                </div>
              </div>
            ) : null}
          </section>

          <section className="job-summary-card">
            <div className="job-summary-card__header">
              <h2>Publishing summary</h2>
              <p>A quick check of what candidates will see.</p>
            </div>
            <dl className="job-publishing-summary">
              <div>
                <dt>Company</dt>
                <dd>{companyName || item.company || 'Not set'}</dd>
              </div>
              <div>
                <dt>Client</dt>
                <dd>{selectedClientName}</dd>
              </div>
              <div>
                <dt>Workplace</dt>
                <dd>{item.location || 'Not set'}</dd>
              </div>
              <div>
                <dt>Hiring region</dt>
                <dd>{formatJobRegion(item)}</dd>
              </div>
              <div>
                <dt>Compensation</dt>
                <dd>{formatJobCompensation(item)}</dd>
              </div>
              <div>
                <dt>Matching skills</dt>
                <dd>{skills.length || 'None yet'}</dd>
              </div>
            </dl>
            {!isDeleted ? (
              <div className="job-summary-card__actions">
                {item.id ? (
                  <button
                    type="button"
                    className="job-action job-action--secondary"
                    onClick={scrollToFlyer}
                  >
                    <FileImageOutlined aria-hidden="true" />
                    Review social flyer
                  </button>
                ) : null}
                {item.id ? (
                  <button
                    type="button"
                    className="job-action job-action--quiet job-action--danger"
                    disabled={isSubmitting}
                    onClick={() => void archiveJob()}
                  >
                    <DeleteOutlined aria-hidden="true" />
                    Archive job
                  </button>
                ) : null}
              </div>
            ) : null}
          </section>
        </aside>
      </div>

      {!isDeleted ? (
        <div className="job-mobile-actions" aria-label="Mobile job actions">
          {!item.published ? (
            <button
              type="button"
              className="job-action job-action--secondary"
              disabled={!isFormModified || !readiness.isReady || isSubmitting}
              onClick={() => void save(item, true, false)}
            >
              <SaveOutlined aria-hidden="true" />
              Save draft
            </button>
          ) : (
            <button
              type="button"
              className="job-action job-action--secondary"
              disabled={!readiness.isReady || isSubmitting}
              onClick={handlePreview}
            >
              <EyeOutlined aria-hidden="true" />
              Preview
            </button>
          )}
          <button
            type="button"
            className="job-action job-action--primary"
            disabled={!readiness.isReady || isSubmitting}
            onClick={() => void save(item, false, true)}
          >
            <CloudUploadOutlined aria-hidden="true" />
            {isSubmitting ? primaryActionLoadingLabel : primaryActionLabel}
          </button>
        </div>
      ) : null}
    </main>
  );
};

export default JobForm;
